import React, { useEffect, useState } from 'react';
import { Modal, Form, Button, Row, Col } from 'react-bootstrap';
import { ServerPath } from '../../Constant';
import { toast } from 'react-toastify';
import { _axios } from '../../utils/axios';

const CreateNotification = ({ show, onHide }) => {
    // State matching the new multi-language and scheduling requirements
    const [formData, setFormData] = useState({
        languages: { english: true, hindi: false, marathi: false, gujarati: false },
        titles: { english: '', hindi: '', marathi: '', gujarati: '' },
        descriptions: { english: '', hindi: '', marathi: '', gujarati: '' },
        status: 'Draft',
        action_link: '',
        thumbnail_url: '',
        audience_selection: 'All',
        platform_targets: [],
        activity_range: '',
        isAutoScheduled: false,
        scheduledDate: '',
        scheduledTime: ''
    });

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [stats, setStats] = useState({}); // Initialized as object to match backend service
    const [targetUser, setTargetUser] = useState(0);

    // Fetches user statistics whenever the modal is shown
    const fetchStats = async () => {
        try {
            const { data } = await _axios.get(`${ServerPath}/admin-notification/stats`);
            if (data.success === 1) {
                setStats(data.data);
                // Default target count to Total if 'All' is selected
                if (formData.audience_selection === 'All') {
                    setTargetUser(data.data.total_notifiable_users);
                }
            }
        } catch (err) {
            console.error("Stats Fetch Error:", err);
        }
    };

    useEffect(() => {
        if (show) fetchStats();
    }, [show]);

    const handleInputChange = (e, category, lang) => {
        const { name, value, type, checked } = e.target;

        setFormData(prev => {
            // Logic for multi-language checkboxes
            if (type === 'checkbox' && category === 'languages') {
                return { ...prev, languages: { ...prev.languages, [name]: checked } };
            }
            // Logic for localized titles and descriptions
            if (category) {
                return { ...prev, [category]: { ...prev[category], [lang]: value } };
            }
            // Logic for platform selection
            if (type === 'checkbox' && name === 'platform') {
                const platforms = prev.platform_targets.includes(value)
                    ? prev.platform_targets.filter(p => p !== value)
                    : [...prev.platform_targets, value];

                // Update dynamic target count
                let count = 0;
                if (platforms.includes('iOS')) count += (stats?.ios_users || 0);
                if (platforms.includes('Android')) count += (stats?.android_users || 0);
                setTargetUser(count);

                return { ...prev, platform_targets: platforms };
            }
            return { ...prev, [name]: value };
        });
    };
    const handleSubmit = async (finalStatus) => {
        const adminName = localStorage.getItem('Name');

        // 1. Dynamic Validation for Selected Languages
        const selectedLanguages = Object.keys(formData.languages).filter(lang => formData.languages[lang]);

        // Ensure English is always handled if it's your primary requirement
        if (selectedLanguages.length === 0) {
            return toast.error("Please select at least one language");
        }

        for (const lang of selectedLanguages) {
            const title = formData.titles[lang];
            const description = formData.descriptions[lang];

            if (!title || !description) {
                // Capitalize first letter for the error message
                const langLabel = lang.charAt(0).toUpperCase() + lang.slice(1);
                return toast.error(`${langLabel} Title and Description are required`);
            }
        }

        // 2. Validate Action Link
        if (!formData.action_link) {
            return toast.error("Action Link is required");
        }

        // 3. Scheduling Validation
        if (formData.isAutoScheduled && (!formData.scheduledDate || !formData.scheduledTime)) {
            return toast.error("Please provide both Date and Time for scheduling");
        }

        try {
            setIsSubmitting(true);

            // Helper to get value only if language is selected
            const getVal = (category, lang) => formData.languages[lang] ? formData[category][lang] : null;

            const payload = {
                // Use helper to ensure only SELECTED languages are sent
                title: getVal('titles', 'english'),
                title_hin: getVal('titles', 'hindi'),
                title_mar: getVal('titles', 'marathi'),
                title_guj: getVal('titles', 'gujarati'),

                body: getVal('descriptions', 'english'),
                body_hin: getVal('descriptions', 'hindi'),
                body_mar: getVal('descriptions', 'marathi'),
                body_guj: getVal('descriptions', 'gujarati'),

                action_link: formData.action_link,
                image_url: formData.thumbnail_url,
                audience_type: formData.audience_selection === 'Platform' ? formData.platform_targets.join(',') :
                    formData.audience_selection === 'Activity' ? `Activity-${formData.activity_range}` : 'All',
                status: finalStatus,
                is_scheduled: formData.isAutoScheduled ? 1 : 0,
                scheduled_at: formData.isAutoScheduled ? `${formData.scheduledDate} ${formData.scheduledTime}:00` : null,
                created_by: adminName,
                target_user: targetUser
            };

            const res = await _axios.post(`${ServerPath}/admin-notification`, payload);
            if (res.data.success) {
                toast.success(`Notification ${formData.isAutoScheduled ? 'Scheduled' : 'Published'} successfully`);
                onHide();
            }
        } catch (err) {
            console.log("Notification Creation Error:", err);
            toast.error("Failed to process notification");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal show={show} onHide={onHide} size="xl" centered scrollable>
            <Modal.Header closeButton className="border-0">
                <Modal.Title className="fw-bold">Create Notification Template</Modal.Title>
            </Modal.Header>
            <Modal.Body className="px-4">

                {/* 1. Language Selection & Status */}
                <Row className="mb-4">
                    <Col md={8}>
                        <Form.Label className="fw-semibold">Select Notification Languages <span className="text-danger">*</span></Form.Label>
                        <div className="d-flex gap-4">
                            {['English', 'Hindi', 'Marathi', 'Gujarati'].map(lang => (
                                <Form.Check
                                    key={lang}
                                    type="checkbox"
                                    label={lang}
                                    name={lang.toLowerCase()}
                                    checked={formData.languages[lang.toLowerCase()]}
                                    onChange={(e) => handleInputChange(e, 'languages')}
                                />
                            ))}
                        </div>
                    </Col>
                    <Col md={4}>
                        <Form.Label className="fw-semibold">Status</Form.Label>
                        <Form.Select name="status" value={formData.status} onChange={(e) => handleInputChange(e)}>
                            <option value="Draft">Draft</option>
                            <option value="Published">Published</option>
                        </Form.Select>
                    </Col>
                </Row>

                {/* 2. Notification Titles with Stats */}
                <Form.Label className="fw-semibold mb-2">Notification Title <span className="text-danger">*</span></Form.Label>
                <Row className="g-3 mb-4">
                    {[
                        { label: 'English', key: 'english', stat: 'users_english' },
                        { label: 'Hindi', key: 'hindi', stat: 'users_hindi' },
                        { label: 'Marathi', key: 'marathi', stat: 'users_marathi' },
                        { label: 'Gujarati', key: 'gujarati', stat: 'users_gujarati' }
                    ].map(item => (
                        <Col md={3} key={item.key}>
                            <Form.Label className="small mb-1">
                                {item.label} Title <span style={{ color: '#5662F6', fontWeight: 'bold' }}>({stats[item.stat] || 0})</span>
                            </Form.Label>
                            <Form.Control
                                placeholder={`Title in ${item.label}`}
                                value={formData.titles[item.key]}
                                disabled={!formData.languages[item.key]}
                                onChange={(e) => handleInputChange(e, 'titles', item.key)}
                                maxLength={30}
                            />
                                <div className="text-end small text-muted">{formData.titles[item.key]?.length || 0}/30</div>
                        </Col>
                    ))}
                </Row>

                {/* 3. Descriptions */}
                <Form.Label className="fw-semibold mb-2">Description <span className="text-danger">*</span></Form.Label>
                <Row className="g-3 mb-4">
                    {['English', 'Hindi', 'Marathi', 'Gujarati'].map(lang => (
                        <Col md={6} key={lang}>
                            <Form.Label className="small mb-1">Description {lang}</Form.Label>
                            <Form.Control
                                as="textarea" rows={2}
                                placeholder={`Description in ${lang}`}
                                value={formData.descriptions[lang.toLowerCase()]}
                                disabled={!formData.languages[lang.toLowerCase()]}
                                maxLength={100}
                                onChange={(e) => handleInputChange(e, 'descriptions', lang.toLowerCase())}
                            />
                            <div className="text-end small text-muted">{formData.descriptions[lang.toLowerCase()]?.length || 0}/100</div>
                        </Col>
                    ))}
                </Row>

                {/* 4. Operations */}
                <Form.Label className="fw-semibold mb-2">Operations <span className="text-danger">*</span></Form.Label>
                <Row className="g-3 mb-4">
                    <Col md={6}>
                        <Form.Label className="small mb-1">Action Link / URL <span className="text-danger">*</span></Form.Label>
                        <Form.Control name="action_link" placeholder="https://..." value={formData.action_link} onChange={handleInputChange} />
                    </Col>
                    <Col md={6}>
                        <Form.Label className="small mb-1">Thumbnail Link / URL</Form.Label>
                        <Form.Control name="thumbnail_url" placeholder="https://..." value={formData.thumbnail_url} onChange={handleInputChange} />
                    </Col>
                </Row>

                {/* 5. Target Audience Grid */}
                <Form.Label className="fw-semibold mb-2">Target User (Targeting {targetUser} users)</Form.Label>
                <Row className="mb-4">
                    <Col md={4}>
                        <Form.Check type="radio" label={`All Registered (${stats?.total_notifiable_users || 0})`} name="aud" checked={formData.audience_selection === 'All'} onChange={() => { setFormData({ ...formData, audience_selection: 'All' }); setTargetUser(stats.total_notifiable_users); }} />
                    </Col>
                    <Col md={4}>
                        <Form.Check type="radio" label="By Platform" name="aud" checked={formData.audience_selection === 'Platform'} onChange={() => setFormData({ ...formData, audience_selection: 'Platform' })} />
                        <div className="ms-4 mt-2">
                            <Form.Check type="checkbox" label={`iOS (${stats?.ios_users || 0})`} checked={formData.platform_targets.includes('iOS')} disabled={formData.audience_selection !== 'Platform'} onChange={(e) => handleInputChange({ target: { name: 'platform', value: 'iOS', type: 'checkbox', checked: e.target.checked } })} />
                            <Form.Check type="checkbox" label={`Android (${stats?.android_users || 0})`} checked={formData.platform_targets.includes('Android')} disabled={formData.audience_selection !== 'Platform'} onChange={(e) => handleInputChange({ target: { name: 'platform', value: 'Android', type: 'checkbox', checked: e.target.checked } })} />
                        </div>
                    </Col>
                    <Col md={4}>
                        <Form.Check type="radio" label="By Activity" name="aud" checked={formData.audience_selection === 'Activity'} onChange={() => setFormData({ ...formData, audience_selection: 'Activity' })} />
                        <div className="ms-4 mt-2">
                            {['7', '30', '60'].map(days => (
                                <Form.Check key={days} type="radio" name="act" label={`Last ${days} Days (${stats[`active_notifiable_${days}_days`] || 0})`} disabled={formData.audience_selection !== 'Activity'} onChange={() => { setFormData({ ...formData, activity_range: days }); setTargetUser(stats[`active_notifiable_${days}_days`]); }} />
                            ))}
                        </div>
                    </Col>
                </Row>

                {/* 6. Scheduling */}
                <Form.Check
                    type="checkbox" label="Auto Scheduling in Future" className="fw-semibold mb-2"
                    checked={formData.isAutoScheduled}
                    onChange={(e) => setFormData({ ...formData, isAutoScheduled: e.target.checked })}
                />
                <Row className="g-3 mb-4">
                    <Col md={3}>
                        <Form.Label className="small mb-1">Date</Form.Label>
                        <Form.Control
                            type="date"
                            value={formData.scheduledDate}
                            // Prevents selecting any day before today
                            min={new Date().toISOString().split('T')[0]}
                            disabled={!formData.isAutoScheduled}
                            onChange={(e) => setFormData({ ...formData, scheduledDate: e.target.value })}
                        />
                    </Col>
                    <Col md={3}>
                        <Form.Label className="small mb-1">Time</Form.Label>
                        <Form.Control
                            type="time"
                            value={formData.scheduledTime}
                            // If the selected date is today, set min time to 'now'
                            min={
                                formData.scheduledDate === new Date().toISOString().split('T')[0]
                                    ? new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
                                    : undefined
                            }
                            disabled={!formData.isAutoScheduled}
                            onChange={(e) => setFormData({ ...formData, scheduledTime: e.target.value })}
                        />
                    </Col>
                </Row>
            </Modal.Body>
            <Modal.Footer className="border-0 pt-0 pb-4">
                <Button className="px-5 py-2" style={{ backgroundColor: '#6c757d', border: 'none', borderRadius: '8px' }} onClick={() => handleSubmit('Draft')} disabled={isSubmitting}>Save as Draft</Button>
                <Button className="px-5 py-2" style={{ backgroundColor: '#5662F6', border: 'none', borderRadius: '8px' }} onClick={() => handleSubmit('Success')} disabled={isSubmitting}>Publish Now</Button>
            </Modal.Footer>
        </Modal>
    );
};

export default CreateNotification;